import {
  AppAvatar,
  AppBox,
  AppGridBox,
  AppIconButton,
  AppModal,
  AppPaper,
  AppSkeletonBasicInfo,
  AppText,
  AppTitle,
  InfoCard,
} from "../../../../commons/components";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import { UserStatusEnum } from "../../../../commons/enums/user-status-enum";
import IconApproval from "../../../../commons/components/icons/approval";
import IconProhibited from "../../../../commons/components/icons/prohibited";
import { useSnackbar } from "../../../../redux/snackbarProvider";
import { fetchUser } from "../../../../redux/slices/user/manage-user-slice";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../commons/components/interface";
import { useState } from "react";
import UserDeactive from "./UserDeactive";
import { User } from "../../../../commons/models/user";
import IconCheckMarkCircle from "../../../../commons/components/icons/checkmark-circle";
import UserActive from "./UserActive";
import { RolePolicyActionEnum } from "../../../../commons/enums/role-policy-action.enum";
import ability from "../../../../casl/Can";
import { userStatusChange } from "../../../../redux/slices/user/manage-user-edit-slice";
import useAuth from "../../../../hooks/useAuth";

interface UserInfoProps {
  userInfo: User;
}

export default function UserInfo({ userInfo }: UserInfoProps) {
  const { userInfoStatus } = useAppSelector((state) => state.manageUser);
  const { userToggleStatus } = useAppSelector((state) => state.manageUserEdit);
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { user: authUser } = useAuth();
  const [isDeactiveUser, setDeactiveUser] = useState(false);
  const [isActivateUser, setActivateUser] = useState(false);

  const handleUserStatusClick = () => {
    if (userInfo.status === Number(UserStatusEnum.Active)) {
      setDeactiveUser(true);
    } else {
      setActivateUser(true);
    }
  };

  const handleDeactiveConfirm = (e: Event) => {
    e.preventDefault();
    dispatch(userStatusChange({ userId: userInfo.id, status: UserStatusEnum.Deactive }))
      .unwrap()
      .then(() => {
        addSnackbar({
          key: "user-retire-success",
          text: "User Deactived Successfully",
          variant: "success",
        });
        setDeactiveUser(false);
        dispatch(fetchUser(userInfo.id));
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        addSnackbar({
          key: "error",
          text: error.response?.data.message,
          variant: "danger",
        });
      });
  };

  const handleActiveConfirm = (e: Event) => {
    e.preventDefault();
    dispatch(userStatusChange({ userId: userInfo.id, status: UserStatusEnum.Active }))
      .unwrap()
      .then(() => {
        addSnackbar({
          key: "user-active-success",
          text: "User Activated Successfully",
          variant: "success",
        });
        setActivateUser(false);
        dispatch(fetchUser(userInfo.id));
      })
      .catch((error: AxiosError<IServerErrorResponse>) => {
        addSnackbar({
          key: "error",
          text: error.response?.data.message,
          variant: "danger",
        });
      });
  };

  return (
    <>
      {userInfoStatus === StatusEnum.Loading ? (
        <AppSkeletonBasicInfo />
      ) : (
        <AppBox flexDirection="column" gap="md">
          <AppBox flexDirection="column" gap="xs">
            <AppTitle as="h5">Basic Information</AppTitle>
            <AppGridBox columns={3} gap="sm">
              <InfoCard label="Email" title={userInfo.email} alignItems="start" shadow="xs" />
              <InfoCard
                label="User's Status"
                title={userInfo.status === UserStatusEnum.Active ? "Active" : "Retired"}
                icon={
                  userInfo.status === UserStatusEnum.Active ? (
                    <IconApproval height={32} width={32} color="var(--fg-outline-success)" />
                  ) : (
                    <IconProhibited height={32} width={32} color="var(--fg-outline-danger)" />
                  )
                }
                alignItems="start"
                shadow="xs"
                rightSide={
                  ability.can(RolePolicyActionEnum.STATUS, User) &&
                  userInfo.id !== authUser?.id && (
                    <AppIconButton
                      variant="light"
                      icon={
                        userInfo.status === UserStatusEnum.Active ? (
                          <IconProhibited color="var(--fg-outline-danger)" />
                        ) : (
                          <IconApproval color="var(--fg-outline-success)" />
                        )
                      }
                      onClick={handleUserStatusClick}
                    />
                  )
                }
              />
            </AppGridBox>
          </AppBox>
          <AppBox flexDirection="column" gap="xs">
            <AppTitle as="h5">Roles</AppTitle>
            <AppGridBox columns={3} gap="sm">
              {userInfo.roles.length > 0 &&
                userInfo.roles.map((role, index) => (
                  <AppPaper key={index} padding="xs" shadow="xs" radius="sm">
                    <AppBox alignItems="center" style={{ height: "100%" }}>
                      <AppTitle as={"h5"}>{role.name}</AppTitle>
                    </AppBox>
                  </AppPaper>
                ))}
              {userInfo.userCompetitionsRoles.length > 0 &&
                userInfo.userCompetitionsRoles.map((competitionRole, index) => (
                  <AppPaper key={index} padding="xs" shadow="xs" radius="sm">
                    <AppBox justifyContent="space-between" gap="2xs" alignItems="center">
                      <AppBox flexDirection="column">
                        <AppTitle as={"h5"}>{competitionRole.role.name}</AppTitle>
                        <AppText as="label" size="sm" color="muted">
                          {competitionRole.competition.title}
                        </AppText>
                      </AppBox>
                      <AppAvatar
                        username={competitionRole.competition.title}
                        size="sm"
                        src={competitionRole.competition.logo as string}
                      />
                    </AppBox>
                  </AppPaper>
                ))}
              {userInfo.userTeamsRoles.length > 0 &&
                userInfo.userTeamsRoles.map((teamRole, index) => (
                  <AppPaper key={index} padding="xs" shadow="xs" radius="sm">
                    <AppBox justifyContent="space-between" gap="2xs" alignItems="center">
                      <AppBox flexDirection="column">
                        <AppTitle as={"h5"}>{teamRole.role.name}</AppTitle>
                        <AppText as="label" size="sm" color="muted">
                          {teamRole.team.shortName}
                        </AppText>
                      </AppBox>
                      <AppAvatar username={teamRole.team.shortName} size="sm" src={teamRole.team.logo as string} />
                    </AppBox>
                  </AppPaper>
                ))}
              {userInfo.roles.length < 1 &&
                userInfo.userCompetitionsRoles.length < 1 &&
                userInfo.userTeamsRoles.length < 1 && (
                  <AppPaper padding="xs" shadow="xs" radius="sm">
                    <AppTitle as={"h5"}>Just a User</AppTitle>
                  </AppPaper>
                )}
            </AppGridBox>
          </AppBox>
        </AppBox>
      )}
      <AppModal
        opened={isDeactiveUser}
        title="Want To Deactive This User?"
        titleIcon={<IconProhibited />}
        iconColor="danger"
        confirmButtonLabel="Deactive"
        withLoadingButton={userToggleStatus === StatusEnum.Loading}
        onClose={() => {
          setDeactiveUser(false);
        }}
        onSave={(e) => {
          handleDeactiveConfirm(e as Event);
        }}
      >
        <UserDeactive user={userInfo} />
      </AppModal>
      <AppModal
        opened={isActivateUser}
        title="Want To Active This User?"
        titleIcon={<IconCheckMarkCircle />}
        iconColor="success"
        confirmButtonLabel="Active"
        withLoadingButton={userToggleStatus === StatusEnum.Loading}
        onClose={() => {
          setActivateUser(false);
        }}
        onSave={(e) => {
          handleActiveConfirm(e as Event);
        }}
      >
        <UserActive user={userInfo} />
      </AppModal>
    </>
  );
}
